import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { resolveImg } from "@/lib/api";
import { ArrowLeft, ArrowRight, X } from "@phosphor-icons/react";

const EASE = [0.22, 1, 0.36, 1];
const CREAM = "#efe7da";

function metaLine(p) {
  return [p.category_name, p.area, p.year].filter(Boolean).join("  ·  ");
}

export default function ProjectOverlay({ projects, index, onIndexChange, onClose }) {
  const project = projects[index];
  const [imgIdx, setImgIdx] = useState(0);
  const stripRef = useRef(null);
  const thumbRefs = useRef([]);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const gallery = useMemo(() => {
    if (!project) return [];
    const all = [project.cover_image, ...(project.images || [])].filter(Boolean);
    return [...new Set(all)];
  }, [project]);

  // reset gallery position when switching projects
  useEffect(() => { setImgIdx(0); }, [index]);

  const nextImg = useCallback(() => setImgIdx((i) => (i + 1) % gallery.length), [gallery.length]);
  const prevImg = useCallback(() => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length), [gallery.length]);
  const nextProject = useCallback(() => onIndexChange((index + 1) % projects.length), [index, projects.length, onIndexChange]);
  const prevProject = useCallback(() => onIndexChange((index - 1 + projects.length) % projects.length), [index, projects.length, onIndexChange]);

  // body scroll lock + keyboard controls
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") nextImg();
      else if (e.key === "ArrowLeft") prevImg();
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [onClose, nextImg, prevImg]);

  // refresh whether the thumbnail strip can scroll left / right
  const updateArrows = useCallback(() => {
    const s = stripRef.current;
    if (!s) return;
    setCanLeft(s.scrollLeft > 4);
    setCanRight(s.scrollLeft + s.clientWidth < s.scrollWidth - 4);
  }, []);

  // keep the active thumbnail in view when navigating, and refresh arrows
  useEffect(() => {
    const s = stripRef.current;
    const el = thumbRefs.current[imgIdx];
    if (s && el) {
      const elLeft = el.offsetLeft;
      const elRight = elLeft + el.offsetWidth;
      if (elLeft < s.scrollLeft) s.scrollTo({ left: elLeft - 16, behavior: "smooth" });
      else if (elRight > s.scrollLeft + s.clientWidth) s.scrollTo({ left: elRight - s.clientWidth + 16, behavior: "smooth" });
    }
    const t = setTimeout(updateArrows, 400);
    return () => clearTimeout(t);
  }, [imgIdx, gallery.length, updateArrows]);

  if (!project) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        data-testid="project-overlay"
        className="fixed inset-0 z-[120] flex items-center justify-center p-3 md:p-6"
        style={{ background: "rgba(24,20,17,0.92)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        onClick={onClose}
      >
        <motion.div
          className="relative w-full max-w-[1500px] h-[92vh] overflow-hidden rounded-xl flex flex-col md:flex-row"
          style={{ background: CREAM }}
          initial={{ opacity: 0, scale: 0.97, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.5, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Left: image + thumbnails */}
          <div className="flex flex-col md:w-[64%] h-1/2 md:h-full">
            <div className="relative flex-1 overflow-hidden bg-neutral-900">
              <AnimatePresence mode="wait">
                <motion.img
                  key={imgIdx}
                  data-testid="overlay-image"
                  src={resolveImg(gallery[imgIdx])}
                  alt={`${project.title} — ${imgIdx + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: EASE }}
                />
              </AnimatePresence>

              {gallery.length > 1 && (
                <>
                  <CircleBtn testid="overlay-prev-image" onClick={prevImg} className="left-4 md:left-6">
                    <ArrowLeft size={20} weight="light" />
                  </CircleBtn>
                  <CircleBtn testid="overlay-next-image" onClick={nextImg} className="right-4 md:right-6">
                    <ArrowRight size={20} weight="light" />
                  </CircleBtn>
                  <div
                    data-testid="overlay-counter"
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[11px] tracking-[0.2em] text-white/90"
                    style={{ background: "rgba(20,17,15,0.72)" }}
                  >
                    {String(imgIdx + 1).padStart(2, "0")} / {String(gallery.length).padStart(2, "0")}
                  </div>
                </>
              )}
            </div>

            {/* thumbnails */}
            {gallery.length > 1 && (
              <div className="flex items-center gap-3 px-4 md:px-6 py-4" style={{ background: CREAM }}>
                <button
                  data-testid="thumb-scroll-left"
                  onClick={() => stripRef.current?.scrollBy({ left: -240, behavior: "smooth" })}
                  className={`shrink-0 text-foreground/50 hover:text-foreground transition-all duration-300 ${canLeft ? "opacity-100" : "opacity-20 pointer-events-none"}`}
                  aria-label="Scroll thumbnails left"
                >
                  <ArrowLeft size={20} weight="light" />
                </button>
                <div ref={stripRef} onScroll={updateArrows} className="flex-1 flex gap-3 overflow-x-auto scrollbar-hide">
                  {gallery.map((img, i) => (
                    <button
                      key={i}
                      ref={(el) => (thumbRefs.current[i] = el)}
                      data-testid={`overlay-thumb-${i}`}
                      onClick={() => setImgIdx(i)}
                      className="relative shrink-0 w-20 h-14 md:w-28 md:h-[72px] overflow-hidden rounded-sm transition-all duration-300"
                      style={{ outline: i === imgIdx ? "1.5px solid #b08d57" : "1.5px solid transparent", outlineOffset: 2 }}
                    >
                      <img src={resolveImg(img)} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
                      {i !== imgIdx && <span className="absolute inset-0 bg-black/20" />}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="thumb-scroll-right"
                  onClick={() => stripRef.current?.scrollBy({ left: 240, behavior: "smooth" })}
                  className={`shrink-0 text-foreground/50 hover:text-foreground transition-all duration-300 ${canRight ? "opacity-100" : "opacity-20 pointer-events-none"}`}
                  aria-label="Scroll thumbnails right"
                >
                  <ArrowRight size={20} weight="light" />
                </button>
              </div>
            )}
          </div>

          {/* Right: info panel */}
          <div className="relative md:w-[36%] h-1/2 md:h-full flex flex-col px-7 md:px-12 py-8 md:py-14 overflow-y-auto">
            <button
              data-testid="overlay-close"
              onClick={onClose}
              className="absolute top-6 right-6 md:top-8 md:right-8 w-11 h-11 rounded-full border border-foreground/25 flex items-center justify-center text-foreground/70 hover:bg-foreground hover:text-background transition-colors duration-300"
              aria-label="Close"
            >
              <X size={18} weight="light" />
            </button>

            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.3em] text-accent">Selected Project</p>
              <h2 data-testid="overlay-title" className="mt-5 md:mt-7 font-serif font-light text-5xl md:text-6xl leading-[0.98] tracking-tight pr-12">
                {project.title}
              </h2>
              {project.location && (
                <p className="mt-4 font-serif text-2xl md:text-3xl text-accent/90">{project.location}</p>
              )}
              <p className="mt-7 text-[11px] uppercase tracking-[0.2em] text-foreground/55">{metaLine(project)}</p>

              <div className="mt-8 h-px w-full bg-foreground/15" />

              <p className="mt-8 text-[15px] md:text-base leading-[1.85] text-foreground/70 max-w-md">
                {project.description || project.excerpt}
              </p>
            </div>

            {/* project navigation */}
            <div className="mt-10 flex items-center justify-between">
              <button
                data-testid="overlay-prev-project"
                onClick={prevProject}
                className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-foreground/70 hover:text-foreground transition-colors"
              >
                <ArrowLeft size={16} weight="light" className="transition-transform duration-300 group-hover:-translate-x-1" />
                Previous
              </button>
              <button
                data-testid="overlay-next-project"
                onClick={nextProject}
                className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-foreground/70 hover:text-foreground transition-colors"
              >
                Next
                <ArrowRight size={16} weight="light" className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function CircleBtn({ children, onClick, className, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white/90 backdrop-blur-sm transition-all duration-300 hover:scale-105 ${className}`}
      style={{ background: "rgba(30,26,22,0.55)" }}
    >
      {children}
    </button>
  );
}
